import ControllerBase from './../../../../global/js/ControllerBase';
import { YouTubePlayer as YouTubePlayerPlayer, Options } from 'youtube-player/dist/types';
import { VideoData } from './o-video.data';

// see https://github.com/gajus/youtube-player/blob/879eab4ac23ff12509cbfc1363d4c3cd138a1d03/examples/src/youtube-player.js
import YouTubePlayer from 'youtube-player';
import PlayerStates from 'youtube-player/dist/constants/PlayerStates';
import { BehaviorSubject, fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IOneTrustService, OneTrustDomainData, OneTrustService } from './o-video.cookieservice';
import { MoleculeVideoOverlayEvents, MoleculeVideoOverlayEventTypes } from 'wolf/components/molecules/m-video-overlay/m-video-overlay.events';
import { OrganismVideoEvents, OrganismVideoEventTypes } from './o-video.events';
import { ControllerUnmount } from 'global/interfaces/controller.interface';
global ? (global['YouTubePlayer'] = YouTubePlayer) : undefined;

interface ComponentRefs {
	posterContainer: 'posterContainer',
	loadingOverlay: 'loadingOverlay',
	playerContainer: 'playerContainer',
	brickOverlay: 'brickOverlay',
	cookieOverlay: 'cookieOverlay',
	cookieButtonWrapper: 'cookieButtonWrapper',
}
/**
 * Youtube video player using the Youtube IFrame Player API
 * @url https://yarnpkg.com/package/youtube-player#examples
 * @types youtube-player
 * @example https://github.com/gajus/youtube-player/blob/879eab4ac23ff12509cbfc1363d4c3cd138a1d03/examples/src/playing-video/index.html
 *
 * OneTrust Cookie handling
 * 1. Check if 'C0004' exists in the window.OnetrustActiveGroups
 * 2a. If it does, initialize the video as normal (scripts load and user will see play button).
 * 2b. If it does not, show our custom overlay about cookies.
 * 3. When the user clicks our overlay button, toggle the OneTrust overlay: OneTrust.ToggleInfoDisplay()
 * 4. Listen for: OneTrust.OnConsentChanged callback. When the user accepts the cookies, hide the OneTrust overlay and load the video (like in 2a).
 */
export default class ControllerVideo extends ControllerBase<ComponentRefs> implements ControllerUnmount {

	private static componentRefs: ComponentRefs = {
		posterContainer: 'posterContainer',
		loadingOverlay: 'loadingOverlay',
		playerContainer: 'playerContainer',
		brickOverlay: 'brickOverlay',
		cookieOverlay: 'cookieOverlay',
		cookieButtonWrapper: 'cookieButtonWrapper'
	}

	/** The container with poster bg, play and loading */
	private posterContainer: HTMLDivElement;
	/** The loading spinner */
	private loadingOverlay: HTMLDivElement;
	/** The main youtube video container */
	private playerContainer: HTMLDivElement;
	/** The custom design overlay for the feature */
	private brickOverlay: HTMLDivElement;
	/** The overlay which shows so user can consent to cookies */
	private cookieOverlay: HTMLDivElement;
	/** The cookie button wrapper */
	private cookieButtonWrapper: HTMLDivElement;
	/** The cookie button (for clicks to accept cookies) */
	private cookieButton: HTMLAnchorElement | undefined = undefined;
	/** The main player element */
	private runningInit: boolean;


	private globalVideoObject = 'ControllerVideo__videosList';
	private player: YouTubePlayerPlayer | undefined;
	private playerInstanceId: BehaviorSubject<string>;
	private playerReady: BehaviorSubject<boolean>;
	/** Event fired when this script has been injected into the page: https://www.youtube.com/iframe_api */
	private youtubeScriptLoaded: BehaviorSubject<boolean>;

	private youtubeIframeApiReady: BehaviorSubject<string>;

	/** The external video data */
	private readonly video: VideoData;

	/** Bypasses the need to consent to cookies */
	private cookieConsentBypass = false;

	/** Subscription management for cookie button click */
	private unsubscribeCookieClick: Subject<any>;

	/** Subscription management */
	private _unsubscribeAll: Subject<any>;

	oneTrustService: IOneTrustService;

	constructor(element: HTMLElement) {
		super(element, [], Object.values(ControllerVideo.componentRefs), true);

		this.unsubscribeCookieClick = new Subject();

		this.playerReady = new BehaviorSubject(false);
		this.playerInstanceId = new BehaviorSubject('');
		this.youtubeScriptLoaded = new BehaviorSubject(false);
		this.youtubeIframeApiReady = new BehaviorSubject('');
		this.runningInit = false;

		// this.playerInstanceId.next((1000 * Math.random()).toString().replace('.', ''));

		// handle subscriptions
		this._unsubscribeAll = new Subject();

		this.handleVideoPlayerReady = this.handleVideoPlayerReady.bind(this);
		this.createNewPlayer = this.createNewPlayer.bind(this);

		this.manuallyGetRefs();

		// check cookie consent status from data
		this.cookieConsentBypass = Object.prototype.hasOwnProperty.call(this.$element.dataset, 'consent')
		//? this.$element.dataset.consent === 'true'
		//: false;

		// get video data from elem data attrib
		const data = this.$element.dataset?.video || '';
		if (data) {
			this.video = JSON.parse(this.$element.dataset?.video || '');

			const _window = window as any;

			if (this.video.videoId) {
				if (_window.OneTrust) {
					this.tryToInitVideo();
				} else {
					window.addEventListener('load', (e) => {
						let i = 0;

						const refreshId = setInterval(() => {
							i++;
							if (_window.OneTrust) {
								this.tryToInitVideo();
								clearInterval(refreshId);
							}

							if (i > 90) {
								clearInterval(refreshId);
							}
						}, 1000);
					});
				}
			}
		}
	}

	private tryToInitVideo(): void {
		// store the player instance as a unique ref to the video
		this.playerInstanceId.next(this.video.videoId);
		this.setPlayerEvents();
		this.setCookieListeners();
		// observe pause-all/stop-all events (so external components can tell the video to stop)
		this.observeExternalVideoEvents();
		this.init();
	}

	private manuallyGetRefs(): void {
		for (const key in ControllerVideo.componentRefs) {
			if (Object.prototype.hasOwnProperty.call(ControllerVideo.componentRefs, key)) {
				const ref = ControllerVideo.componentRefs[key];
				const test = Array.from<HTMLDivElement>(this.$element.querySelectorAll('div[data-ref=' + ref + ']'));
				if (test.length > 0) {
					this[ref] = test[0];
				}
			}
		}

		if (this.cookieButtonWrapper) {
			const cookieButtons = this.cookieButtonWrapper.querySelectorAll<'a'>('a');
			if (cookieButtons.length === 1) {
				this.cookieButton = cookieButtons[0];
			}
		}

		// const _playerContainers = Array.from<HTMLDivElement>(this.$element.querySelectorAll('div[data-ref=' + ControllerVideo.componentRefs.playerContainer + ']'));
		// const playerContainer = _playerContainers.length > 0 ? _playerContainers[0] : undefined;
		// if (playerContainer) {
		// 	this.playerContainer = playerContainer;
		// }

		// const _posterContainer = Array.from<HTMLDivElement>(this.$element.querySelectorAll('div[data-ref=' + ControllerVideo.componentRefs.posterContainer + ']'));
		// const posterContainer = _posterContainer.length > 0 ? _posterContainer[0] : undefined;
		// if (posterContainer) {
		// 	this.posterContainer = posterContainer;
		// }

		// this.posterContainer = this.$references.posterContainer as HTMLDivElement;
		// this.loadingOverlay = this.$references.loadingOverlay as HTMLDivElement;


		// // this.playerContainer = this.$references.playerContainer as HTMLDivElement;

		// this.brickOverlay = this.$references.brickOverlay as HTMLDivElement;
		// this.cookieOverlay = this.$references.cookieOverlay as HTMLDivElement;
		// this.cookieButtonWrapper = this.$references.cookieButtonWrapper as HTMLDivElement;
		// if (this.cookieButtonWrapper) {
		// 	const cookieButtons = this.cookieButtonWrapper.querySelectorAll<'a'>('a');
		// 	cookieButtons.length === 1 ? this.cookieButton = cookieButtons[0] : undefined;
		// }
	}

	private getVideoContainerName(videoId: VideoData['videoId'], instanceId: VideoData['instanceId']): string {
		return ['video', videoId, (instanceId || '')].join('_');
	}

	private setPlayerEvents(): void {
		fromEvent(window, OrganismVideoEvents.iframeAPIReady)
			.pipe(
				takeUntil(this._unsubscribeAll),
			)
			.subscribe((e: CustomEvent<
				OrganismVideoEventTypes['iframeAPIReady']
			>) => {
				if (e && e.detail) {
					const videosList: Record<string, VideoData> = (window as any)[this.globalVideoObject] || {};
					// load videos which are not there yet for this video ID only
					const id = this.video.videoId;
					const instanceId = this.video.instanceId || '';
					for (const videoId in videosList) {
						if (Object.prototype.hasOwnProperty.call(videosList, videoId)) {
							const videoContainerName = this.getVideoContainerName(videoId, instanceId);
							const videoContainer = document.getElementById(videoContainerName) as HTMLDivElement;
							if (videoContainer && videoContainer.children.length === 0 && videoId === id) {
								// console.log('loading video', videoId);
								this.initVideoPlayer(videoContainer, (window as any)[this.globalVideoObject][videoId]);
							}
						}
					}

					// console.log('load video?', this.playerInstanceId.getValue());
					// console.log(window[this.globalVideoObject]);

					// build video iframes
					// ID = "video_RvRhUHTV_8k";
				}
			});

		this.youtubeScriptLoaded
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe((hasLoaded) => {
				if (hasLoaded) {
					// console.log('youtube script loaded');
					// this.handleVideoPlayerReady();
				}
			});

		this.youtubeIframeApiReady
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe((isReady) => {
				if (isReady) {
					// console.log('onYouTubeIframeAPIReady iframe API is ready');
					// this.initVideoPlayer();
				}
			});

		this.playerReady
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe((isReady) => {
				if (isReady) {
					// this.initVideoPlayer();
				}
			});
	}

	private setCookieListeners(): void {
		const _window = window as any;

		// map global service API to local scoped functions
		if (OneTrustService) {
			// register service
			this.oneTrustService = OneTrustService;
			// register callback for consent

			this.oneTrustService.OnConsentChanged = (e) => this.consentChanged(e);

			// register callback for display
			this.oneTrustService.ToggleInfoDisplay = () => this.toggleOneTrustOverlay();
			OneTrustService.OnConsentChanged = (e: any) => this.consentChanged(e);

			this.oneTrustService.GetDomainData = () => this.getDomainData();
		}

		//override the window function for OnConsentChanged. This will be from Storybook or from OneTrust, depending on the env
		if (_window.OneTrust) {
			_window.OneTrust.OnConsentChanged((e) => {
				this.consentChanged(e);
			});
		}


	}

	/**
	 * Return true if the user has given cookie consent
	 * @tbc (Currently set from external data)
	 * @returns boolean
	 */
	private hasGivenCookieConsent = (): boolean => OneTrustService.hasGivenConsent();


	/**
	 * Toggle the one trust overlay
	 * @returns void
	 */
	private toggleOneTrustOverlay(): void {
		// this comes from storybook preview-body.html scripts or live
		window && (window as any).OneTrust && (window as any).OneTrust.ToggleInfoDisplay();
	}

	private getDomainData(): OneTrustDomainData | undefined {
		let data: OneTrustDomainData | undefined = undefined;
		if (window && (window as any).OneTrust) {
			data = (window as any).OneTrust.GetDomainData();
		}
		return data;
	}

	private setConsentProfile(e: any): void {
		window && (window as any).OneTrust && (window as any).OneTrust.setConsentProfile(e);
	}

	/**
	 * Event triggered when the user interacts with OneTrust cookie settings on the UI
	 * @param  {any} e The event. e.detail is like: ",C0001," (if user only accepts necessary cookies) or; ",C0001,C0002,C0003,C0004," (if they accept all)
	 * @returns void
	 */
	private consentChanged(e: any): void {
		if (this.hasGivenCookieConsent()) {
			this.init();
		} else {
			// console.log('OneTrust Cookie consent not given');
		}
	}

	private hasConsent(): boolean {
		return this.cookieConsentBypass === true && this.hasGivenCookieConsent();
	}



	private init(): void {

		if (this.hasConsent()) {
			//console.log('Has cookie consent is true...');
			// hide overlay
			document.querySelectorAll('.o-video__cookieNotice').forEach(item => {
				item.classList.add('uk-hidden');
			});
			// this.hide<typeof this.cookieOverlay>(this.cookieOverlay); // it hides cookie notice only on the first video
			this.load();
			this.dynamicLoadAdditionalPlayers();
		}
		else {
			if (this.runningInit) return;
			this.runningInit = true;
			//console.log('Has cookie consent is false...');
			// show cookie message and wait for consent
			this.show<typeof this.cookieOverlay>(this.cookieOverlay);
			this.observeCookieAuthorise();
			document.querySelectorAll('.o-video__cookieNotice').forEach(item => {
				item.classList.remove('uk-hidden');
			});
		}
	}

	/**
	 * Load any video players which were not mounted, such as one's injected into the DOM after the youtube script loaded
	 * @returns void
	 */
	private dynamicLoadAdditionalPlayers(): void {
		if (this.playerContainer && this.playerContainer.children.length === 0) {
			this.initVideoPlayer(this.playerContainer, (window as any)[this.globalVideoObject][this.video.videoId]);
		}
		let c: Date;
	}

	/**
	 * Listen for stop-all events on the window
	 * @returns void
	 */
	private observeExternalVideoEvents(): void {
		fromEvent(window, OrganismVideoEvents.stopAll)
			.pipe(
				takeUntil(this._unsubscribeAll),
			)
			.subscribe((e: CustomEvent<
				OrganismVideoEventTypes['stopAll']
			>) => {
				if (e && e.detail) {
					// stop the video
					this.stop();
				}
			});

		fromEvent(window, OrganismVideoEvents.pauseAll)
			.pipe(
				takeUntil(this._unsubscribeAll),
			)
			.subscribe((e: CustomEvent<
				OrganismVideoEventTypes['pauseAll']
			>) => {
				if (e && e.detail) {
					// pause the video
					this.pause();
				}
			});

		// fromEvent(window, '')
		// .pipe(
		// 	takeUntil(this._unsubscribeAll),
		// )
		// .subscribe((e: CustomEvent<
		// 	OrganismVideoEventTypes['stopAll']
		// >) => {
		// 	if (e && e.detail) {
		// 		// stop the video
		// 		this.stop();
		// 	}
		// });
	}

	/**
	 * Run once cookie consent has been given
	 * @returns void
	 */
	private load(): void {


		// setup script loaded listener
		// @todo - doesn't work?
		// window.addEventListener('onYoutubeScriptLoaded', () => {
		// 	console.log('onYoutubeScriptLoaded');
		// 	this.youtubeScriptLoaded.next(true);
		// });
		if (!(window as any).onYouTubeIframeAPIReady) {
			/**
			 * @link https://developers.google.com/youtube/iframe_api_reference
			 * onYouTubeIframeAPIReady – The API will call this function when the page has finished downloading the JavaScript for the player API, which
			 * enables you to then use the API on your page. Thus, this function might create the player objects that you want to display when the page loads.
			 */
			(window as any).onYouTubeIframeAPIReady = () => {
				this.dispatchCustomEvent(OrganismVideoEvents.iframeAPIReady, true, true, true);

				// _window.dispatchEvent(new CustomEvent(eventName));
				// this.youtubeIframeApiReady.next(this.playerInstanceId.getValue());
			};

		}

		const newVideosList = Object.assign({}, window[this.globalVideoObject] || {});
		newVideosList[this.playerInstanceId.getValue()] = this.video;

		window[this.globalVideoObject] = newVideosList;

		// load video scripts
		this.prepareVideoPlayerScripts();
	}

	/**
	 * Listen for when a user gives cookie consent by clicking the consent button. On click, hide overlay and init the video
	 * @returns void
	 */
	private observeCookieAuthorise(): void {
		if (!this.cookieButton) return;

		// observe button clicks
		fromEvent(this.cookieButton, 'click')
			.pipe(
				takeUntil(this.unsubscribeCookieClick || this._unsubscribeAll),
			)
			.subscribe((e) => {

				e.preventDefault();

				if (this.oneTrustService) {

					// rather than toggle the info display (e.g. the OneTrust panel) we can simply set the marketing cookie opt-in directly
					const cookiesSet = this.confirmCookieVideoPrefs();
					if (cookiesSet) {
						// hide overlay
					}
					// show the OneTrust cookie overlay
					// this.oneTrustService.ToggleInfoDisplay();

					// stop observing
					// this.unsubscribeCookieClick.next(true);
				}
			});
	}

	private confirmCookieVideoPrefs(): boolean {
		const oneTrustDomainData = this.oneTrustService.GetDomainData();
		if (oneTrustDomainData && oneTrustDomainData.Groups && oneTrustDomainData.Groups.length > 0) {
			const group = oneTrustDomainData.Groups.find(g => g.CustomGroupId === 'C0004');
			if (!group) return false;

			// TODO: this section can be simplified since we do not need the purposeID anymore, just the customGroupId to set the cookie in the setConsentProfile function below
			const { PurposeId } = group;

			// OneTrust.setConsentProfile({"purposes":[{"Id":"7EF94F5E-23AF-4DB8-9A62-E96A07075D29","TransactionType":"CONFIRMED"}]})
			// console.log('Manually setting marketing cookie for the user');

			try {
				this.oneTrustService.setConsentProfile({
					purposes: [
						{
							Id: PurposeId,
							TransactionType: 'CONFIRMED',
						},
					],
				});
				// console.log('Called OneTrust hook so should trigger OnConsentChanged function');

				if ((window as any).OneTrust && (window as any).OneTrust.Init) {
					// user has consented, rerun onetrust init
					(window as any).OneTrust.Init();
				} else {
					// user has consented anyway, bypass
					this.cookieConsentBypass = true;
				}
				this.init();
				return true;

			} catch (error) {
				// console.log('Error manually setting cookie preferences');
				return false;
			}
		}
		// console.log('Could not manually set marketing cookie for the user, the domain groups were empty!');
		return false;
	}

	/**
	 * Add video scripts to the DOM and init iframe
	 */
	private prepareVideoPlayerScripts(): void {

		if (!(window as any).YT) {

			// Append YouTube API Script if it's not already injected into the DOM
			this.appendYoutubeScript();
		}

	}

	/**
	 * Add youtube iframe script to the DOM
	 * @return {HTMLScriptElement}
	 */
	private appendYoutubeScript(): HTMLScriptElement {
		const scriptId = 'youtube-script';
		let script = document.getElementById(scriptId) as HTMLScriptElement;

		if (!script) {

			// add the script if it doesn't yet exist
			script = document.createElement('script');
			script.setAttribute('id', scriptId);

			script.src = 'https://www.youtube.com/iframe_api';

			const firstScriptTag = document.getElementsByTagName('script')[0];
			if (firstScriptTag) {
				firstScriptTag && firstScriptTag.parentNode?.insertBefore(script, firstScriptTag);
			}
		}
		return script;
	}

	/**
	 * Init video player from data
	 */
	private initVideoPlayer(playerContainer: HTMLDivElement, videoData: VideoData): void {

		if (playerContainer) {
			const id = videoData.videoId || this.video.videoId;
			// console.log('Init video instance', this.video.instanceId);

			// Create a new DOM element to initialize the youtube player iframe inside of the player container
			const playerElement = document.createElement('div');

			playerContainer.appendChild(playerElement);

			const playerVars: Options['playerVars'] = {
				// autoplay: this.video.settings.autoplay ? 1 : 0,
				autoplay: 0,
				// @todo - this is the starting time of the video, in seconds
				start: 0,
				controls: this.video.settings.controls ? 1 : 0,
				loop: this.video.settings.loop ? 1 : 0,
				modestbranding: 1, // Gets overriden by YouTube to 0 whenever controls are deactivated
			};
			if (this.video.settings.loop) {
				playerVars.playlist = id; // Video only loops if the player will be initalized as a playlist)
			}

			// setup player options
			const playerOptions: Options = {
				videoId: id,
				playerVars: playerVars,
				// events: {
				// 	// ready: () => this.handleVideoPlayerReady(),
				// 	// stateChange: (e: any) => this.handleVideoStateChange(e),
				// 	// error: (e: any) => this.onVideoError(e),
				// }
			};

			this.createNewPlayer(playerElement, playerOptions, videoData);

		}
	}

	private createNewPlayer(playerElement: HTMLDivElement, playerOptions: Options, videoData: VideoData): void {
		// console.log('youtube iframe ready');
		this.player = YouTubePlayer(playerElement, playerOptions);

		this.player.on('ready', (event) => this.handleVideoPlayerReady(videoData));
		// (this.player as any).on('onReady', (event) => this.handleVideoPlayerReady(event));

		this.player.on('error', (event) => this.onVideoError(event));


		// // set a unique instance ID on the DOM container
		// const newInstanceId = new Date().getTime().toString();
		// this.playerContainer.dataset.instanceid = newInstanceId;
		// this.playerInstanceId.next(newInstanceId);

		// set up a state change callback
		this.player.on('stateChange', (event: CustomEvent & { data: number }) => {
			// console.log(event)
			this.handleVideoStateChange(event);
		});
		this.playerReady.next(true);
	}

	// private createNewPlayer(playerElement: HTMLDivElement, playerOptions: Options): void {
	// 	// new player instance
	// 	// this.player = YouTubePlayer(playerElement, playerOptions);

	// 	// https://developers.google.com/youtube/player_parameters#Embedding_a_Player
	// 	(window as any).onYouTubePlayerAPIReady = () => {
	// 		// console.log('youtube iframe ready');
	// 		this.player = YouTubePlayer(playerElement, playerOptions);

	// 		this.player.on('ready', (event) => this.handleVideoPlayerReady(event));
	// 		// (this.player as any).on('onReady', (event) => this.handleVideoPlayerReady(event));

	// 		this.player.on('error', (event) => this.onVideoError(event));


	// 		// // set a unique instance ID on the DOM container
	// 		// const newInstanceId = new Date().getTime().toString();
	// 		// this.playerContainer.dataset.instanceid = newInstanceId;
	// 		// this.playerInstanceId.next(newInstanceId);

	// 		// set up a state change callback
	// 		this.player.on('stateChange', (event: CustomEvent & { data: number }) => {
	// 			// console.log(event)
	// 			this.handleVideoStateChange(event);
	// 		});
	// 		this.playerReady.next(true);
	// 	}
	// 	// if (!this.player) {
	// 	// }
	// }

	/**
	 * Event listener for player state changes - e.g. when user interacts with the youtube iframe player, youtube fires a state change event
	 * @param  {CustomEvent&{data:number}} event
	 * @returns void
	 */
	private handleVideoStateChange(event: CustomEvent & { data: number }): void {
		switch (event && event.data) {
			case PlayerStates.PLAYING: {
				break;
			}

			case PlayerStates.PAUSED:
			case PlayerStates.ENDED: {
				// show the brick overlay
				this.show<typeof this.brickOverlay>(this.brickOverlay);
				break;
			}
			default:
				break;
		}
	}

	/**
	 * @todo - Intended for video loading errors
	 * @param  {CustomEvent} event
	 * @returns void
	 */
	private onVideoError(event: CustomEvent): void {
		// console.log('Video error', event);
	}

	/**
	 * @todo
	 * Callback to watch for YouTube video iframe ready
	 * Set video player options and show play button
	 */
	private handleVideoPlayerReady(videoData: VideoData): void {
		// Hide Loading Overlay
		setTimeout(() => {
			this.hide<typeof this.loadingOverlay>(this.loadingOverlay);
		}, 500);

		// since we didn't know the height of the video until it was loaded, set the height of the video wrapper
		// based on the width and height attribs of the iframe, now that it has loaded
		// @todo

		// Watch for playing on the inner molecule
		const molecule = this.brickOverlay.querySelector<HTMLDivElement>('div.m-video-overlay');
		if (!molecule) return;

		fromEvent(molecule, MoleculeVideoOverlayEvents.playButtonClicked)
			.pipe(
				takeUntil(this._unsubscribeAll),
			)
			.subscribe((e: CustomEvent<
				MoleculeVideoOverlayEventTypes['playButtonClicked']
			>) => {
				e.preventDefault();
				if (e.detail) {

					// button was clicked
					this.hide<HTMLDivElement>(this.brickOverlay);
					this.hide<HTMLDivElement>(this.posterContainer);
					this.play();
				}
			});
	}

	/**
	 * Starts or resumes the video player
	 * */
	private play(): void {
		this.player && this.player.playVideo();
	}

	private stop(): void {
		this.player && this.player.stopVideo();
	}

	private pause(): void {
		this.player && this.player.pauseVideo();
	}

	/**
	 * Show an element by reference by removing its uk-hidden class
	 * @param  {T} ref
	 * @returns void
	 */
	private show<T extends HTMLElement>(ref: T): void {
		// const elem = this.$references[ref as string] as T;
		ref && ref.classList.remove('uk-hidden');
	}

	/**
	 * Hide an element by reference by adding a uk-hidden class
	 * @param  {T} ref
	 * @returns void
	 */
	private hide<T extends HTMLElement>(ref: T): void {
		// const elem = this.$references[ref];
		ref && ref.classList.add('uk-hidden');
	}

	public onControllerUnmount(): void {
		// console.log('Unmounting component');
		this.player = undefined;

		// remove the player iframe from the DOM
		if (this.posterContainer) {
			while (this.posterContainer.firstChild) {
				this.posterContainer.removeChild(this.posterContainer.firstChild);
			}
		}

		// remove subscribers
		this._unsubscribeAll.next();
		this._unsubscribeAll.complete();
	}
}
